import { useCallback, useEffect, useRef } from 'react';
import { enqueueSnackbar } from 'notistack';
import { useQueryClient } from '@tanstack/react-query';

import useRetrieve from 'src/compass/shared/hooks/reactQuery/useRetrieve';
import useAddMutation from 'src/compass/shared/hooks/reactQuery/useAddMutation';
import useDeleteMutation from 'src/compass/shared/hooks/reactQuery/useDeleteMutation';
import formatPhoneNumber from 'src/compass/shared/util/formatPhoneNumber';
import { useUserPermissions } from 'src/compass/site/routes/useUserPermissions';

import {
  RINGCENTRAL_CALLBACK_CHANNEL,
  RingCentralCallbackMessage,
} from './ringCentralCallbackChannel';

const RINGCENTRAL_URL = 'CurrentUser/RingCentral';

/*
  The code the backend gives a "not connected" or "expired" error
  (RingCentralConnectionService.NotConnectedErrorCode). A send refused for it starts Connect
  RingCentral when the sender presses OK on the error.
*/
export const RINGCENTRAL_NOT_CONNECTED_CODE = 'RingCentralNotConnected';

/*
  Texts go out signed in to the sender's own RingCentral account, from their own line (story 15619),
  so a sender has to connect RingCentral once. The keep-alive keeps the connection fresh; one that
  lapses anyway reads Expired, and every place a text can be sent asks to connect again.
*/
export type RingCentralStatus = 'NotConnected' | 'Connected' | 'Expired';

export type RingCentralConnection = {
  status: RingCentralStatus;
  // E164. The number texts come from, while connected.
  fromNumber?: string | null;
  // False when this environment has no RingCentral app, so nobody can connect.
  isSetUp: boolean;
};

// Whoever can send a text (SCE Reports, Customers) or test one (Configuration) has a connection to
// manage - the same resources CurrentUserController grants these endpoints to. Asking without one of
// them would be a 403, which the app turns into the Not Authorized page.
const RINGCENTRAL_PERMISSIONS = ['SCE_REPORTS', 'CUSTOMERS', 'CONFIGURATION'];

export const formatRingCentralNumber = (fromNumber?: string | null) =>
  fromNumber ? formatPhoneNumber(fromNumber) : '';

export function useRingCentralInvalidate() {
  const queryClient = useQueryClient();
  return useCallback(
    () => queryClient.invalidateQueries({ queryKey: [RINGCENTRAL_URL] }),
    [queryClient]
  );
}

/**
 * The signed-in user's RingCentral connection. Shared by every caller (one query), and refetched
 * when the window regains focus - which is also how a sign-in finished in the popup shows up.
 */
export function useRingCentralConnection() {
  const { userHasPermission } = useUserPermissions();
  const permitted = RINGCENTRAL_PERMISSIONS.some(userHasPermission);

  const { data, isLoading } = useRetrieve({
    url: RINGCENTRAL_URL,
    enabled: permitted,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });

  const connection = data as RingCentralConnection | undefined;

  return {
    permitted,
    connection,
    isLoading: permitted && isLoading,
    connected: connection?.status === 'Connected',
    expired: connection?.status === 'Expired',
  };
}

/**
 * Connect RingCentral: opens RingCentral's sign-in in a popup, and finishes the connection when the
 * popup comes back to /ringcentral/callback.
 */
export function useConnectRingCentral() {
  const invalidate = useRingCentralInvalidate();

  // The state of the sign-in this caller started. RingCentral hands it back with the code, so a
  // callback is taken up only by the button that asked for it - not by every mounted copy of this
  // hook, nor by another tab of the app.
  const pendingStateRef = useRef<string | null>(null);
  const popupRef = useRef<Window | null>(null);

  const { mutate: complete, isPending: completing } = useAddMutation({
    url: `${RINGCENTRAL_URL}/Complete`,
    errorTitle: 'Connect RingCentral failed',
    onSuccess: (data: any) => {
      invalidate();
      enqueueSnackbar(
        `RingCentral connected. Texts will be sent from ${formatRingCentralNumber(
          data?.fromNumber
        )}`,
        { variant: 'success' }
      );
    },
    onError: () => invalidate(),
  });

  const { mutate: start, isPending: starting } = useAddMutation({
    url: `${RINGCENTRAL_URL}/Connect`,
    errorTitle: 'Connect RingCentral failed',
    showLoader: false,
    onSuccess: (data: any) => {
      const authorizeUrl: string = data?.authorizeUrl ?? '';
      pendingStateRef.current = new URL(authorizeUrl).searchParams.get('state');

      const popup = popupRef.current;
      if (popup && !popup.closed) popup.location.href = authorizeUrl;
    },
    onError: () => {
      popupRef.current?.close();
      popupRef.current = null;
    },
  });

  useEffect(() => {
    const channel = new BroadcastChannel(RINGCENTRAL_CALLBACK_CHANNEL);

    channel.onmessage = (event: MessageEvent<RingCentralCallbackMessage>) => {
      const { code, state, error } = event.data ?? {};
      if (!state || state !== pendingStateRef.current) return;

      pendingStateRef.current = null;
      popupRef.current = null;

      if (error || !code) {
        enqueueSnackbar(`RingCentral was not connected${error ? `: ${error}` : '.'}`, {
          variant: 'warning',
        });
        return;
      }

      complete({ code, state });
    };

    return () => channel.close();
  }, [complete]);

  const connect = useCallback(() => {
    // Opened here, in the click itself, so the browser's pop-up blocker lets it through; it is
    // pointed at RingCentral once Connect answers with the sign-in address.
    const popup = window.open('', 'ringcentral-connect', 'width=520,height=720');

    if (!popup) {
      enqueueSnackbar('Allow pop-ups for this site to connect RingCentral.', {
        variant: 'warning',
      });
      return;
    }

    try {
      popup.document.title = 'Connect RingCentral';
      popup.document.body.innerHTML =
        '<p style="font-family: sans-serif; margin: 24px">Opening RingCentral...</p>';
    } catch {
      // The popup from an earlier press, still on RingCentral's page - another origin's document.
    }
    popupRef.current = popup;

    start({});
  }, [start]);

  /*
    For a send's onErrorDismiss: when it was refused because RingCentral is not connected, OK on the
    error goes straight to connecting. Called inside that click, so the popup is allowed.
  */
  const connectIfNotConnected = useCallback(
    (error: unknown) => {
      if ((error as { code?: string } | null)?.code === RINGCENTRAL_NOT_CONNECTED_CODE) connect();
    },
    [connect]
  );

  return { connect, connecting: starting || completing, connectIfNotConnected };
}

/**
 * Disconnect RingCentral: ends the session at RingCentral and forgets the connection.
 */
export function useDisconnectRingCentral() {
  const invalidate = useRingCentralInvalidate();

  const { mutate, isPending } = useDeleteMutation({
    url: RINGCENTRAL_URL,
    errorTitle: 'Disconnect RingCentral failed',
    onSuccess: (data: any) => {
      invalidate();
      enqueueSnackbar(data?.message ?? 'RingCentral disconnected.', { variant: 'success' });
    },
  });

  return { disconnect: () => mutate({}), disconnecting: isPending };
}
